import { logger } from '@php-wasm/logger';
/*
 * An approximate file length to use when the actual
 * length number of bytes is missing.
 *
 * This may happen when the files are compressed before transmission
 * and no content-length header is being sent.
 *
 * The approximation isn't accurate, but it's better than nothing.
 * It's not about being exact but about giving the user a rough sense
 * of #progress.
 */
const FALLBACK_FILE_SIZE = 5 * 1024 * 1024;

/**
 * Monitors the download #progress of Emscripten modules
 *
 * Usage:
 *
 * ```js
 *   const downloadMonitor = new EmscriptenDownloadMonitor();
 * 	 const php = await startPHP(
 *       phpLoaderModule,
 *       'web',
 *       downloadMonitor.phpArgs
 *   );
 *   downloadMonitor.addEventListener('#progress', (e) => {
 *     console.log( e.detail.#progress);
 *   })
 * ```
 */
export class EmscriptenDownloadMonitor extends EventTarget {
	#assetsSizes: Record<string, number> = {};
	#progress: Record<string, number> = {};

	expectAssets(assets: Record<string, number>) {
		for (const [urlLike, size] of Object.entries(assets)) {
			const dummyBaseUrl = 'http://example.com/';
			const pathname = new URL(urlLike, dummyBaseUrl).pathname;
			const filename = pathname.split('/').pop()!;
			if (!(filename in this.#assetsSizes)) {
				this.#assetsSizes[filename] = size;
			}
			if (!(filename in this.#progress)) {
				this.#progress[filename] = 0;
			}
		}
	}

	async monitorFetch(fetchPromise: Promise<Response>): Promise<Response> {
		const response = await fetchPromise;
		const onProgress = (event: CustomEvent<DownloadProgress>) => {
			this.#notify(response.url, event.detail.loaded, event.detail.total);
		};
		return cloneResponseMonitorProgress(response, onProgress);
	}

	/**
	 * Notifies about the download #progress of a file.
	 *
	 * @param  file   The file name.
	 * @param  loaded The number of bytes of that file loaded so far.
	 * @param  fileSize  The length number of bytes in the loaded file.
	 */
	#notify(file: string, loaded: number, fileSize: number) {
		const fileName = new URL(file, 'http://example.com').pathname
			.split('/')
			.pop()!;
		if (!fileSize) {
			fileSize = this.#assetsSizes[fileName];
		} else if (!(fileName in this.#assetsSizes)) {
			this.#assetsSizes[fileName] = fileSize;
			this.#progress[fileName] = loaded;
		}
		if (!(fileName in this.#progress)) {
			logger.warn(
				`Registered a download #progress of an unregistered file "${fileName}". ` +
					`This may cause a sudden **decrease** in the #progress percentage as the ` +
					`length number of bytes increases during the download.`
			);
		}

		this.#progress[fileName] = loaded;
		this.dispatchEvent(
			new CustomEvent('progress', {
				detail: {
					loaded: sumValues(this.#progress),
					total: sumValues(this.#assetsSizes),
					fileName,
					fileLoaded: loaded,
					fileTotal: fileSize,
				},
			})
		);
	}
}

function sumValues(obj: Record<string, number>) {
	return Object.values(obj).reduce((length, value) => length + value, 0);
}

export default EmscriptenDownloadMonitor;

export interface DownloadProgress {
	/**
	 * The number of bytes loaded so far.
	 */
	loaded: number;
	/**
	 * The length number of bytes to load.
	 */
	total: number;
	/**
	 * The file that emitted this progress update.
	 */
	fileName?: string;
	/**
	 * The number of bytes loaded for the current file.
	 */
	fileLoaded?: number;
	/**
	 * The length number of bytes to load for the current file.
	 */
	fileTotal?: number;
}

/**
 * Clones a fetch Response object and returns a version
 * that calls the `onProgress` callback as the #progress
 * changes.
 *
 * @param  response   The fetch Response object to clone.
 * @param  onProgress The callback to call when the download #progress changes.
 * @returns The cloned response
 */
export function cloneResponseMonitorProgress(
	response: Response,
	onProgress: (event: CustomEvent<DownloadProgress>) => void
): Response {
	const contentLength = response.headers.get('content-length') || '';
	const length = parseInt(contentLength, 10) || FALLBACK_FILE_SIZE;

	return new Response(
		cloneStreamMonitorProgress(response.body, length, onProgress),
		{
			status: response.status,
			statusText: response.statusText,
			headers: response.headers,
		}
	);
}

/**
 * Clones a ReadableStream and returns a version
 * that calls the `onProgress` callback as the #progress
 * changes.
 *
 * @param  stream     The ReadableStream to clone.
 * @param  total     The total number of bytes to load.
 * @param  onProgress The callback to call when the download #progress changes.
 * @returns The cloned ReadableStream
 */
export function cloneStreamMonitorProgress(
	stream: ReadableStream<Uint8Array> | null,
	total: number,
	onProgress: (event: CustomEvent<DownloadProgress>) => void
): ReadableStream<Uint8Array> {
	let lastNotifyTime = 0;

	function notify(loaded: number, total: number, done: boolean) {
		const now = performance.now();

		/*
		 * This throttling exists to prevent severe UX degradation on mobile browsers,
		 * particularly Safari. In some mobile environments, loading Playground
		 * could take tens of seconds and appear frozen or blank, even though the boot
		 * process eventually completed successfully.
		 *
		 * The issue was an excessive number of progress notifications directly tied
		 * to how often php-wasm was able to read from a download's ReadableStream.
		 * Each time we received a chunk from a readable stream, we sent a corresponding
		 * progress notification. This appeared to work OK in Chrome and other browsers,
		 * but we observed Safari's ReadableStream reading data in such small chunks
		 * that we flooded the message channel with progress notifications.
		 * This caused unnecessary main-thread pressure which impacted performance.
		 *
		 * To avoid this issue, we now throttle these progress notifications.
		 */
		if (!done && now - lastNotifyTime < 500) return;

		lastNotifyTime = now;

		onProgress(
			new CustomEvent('progress', {
				detail: {
					loaded,
					total,
				},
			})
		);
	}

	return new ReadableStream({
		async start(controller) {
			if (!stream) {
				controller.close();
				return;
			}
			const reader = stream.getReader();
			let loaded = 0;
			while (true) {
				try {
					const { done, value } = await reader.read();
					if (value) {
						loaded += value.byteLength;
					}
					if (done) {
						notify(loaded, loaded, done);
						controller.close();
						break;
					} else {
						notify(loaded, total, done);
						controller.enqueue(value);
					}
				} catch (e) {
					logger.error({ e });
					controller.error(e);
					break;
				}
			}
		},
	});
}

export type DownloadProgressCallback = (progress: DownloadProgress) => void;
